import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import { FormSection } from "@/components/entity-page/FormSection";
import { ConfigField } from "@/components/entity-page/ConfigField";
import { DeleteConfirmPanel } from "@/components/entity-page/DeleteConfirmPanel";
import EditUserForm from "@/components/EditUserForm";
import { EntityAvatar } from "@/components/EntityAvatar";
import { fetchClient, deleteClientUser } from "@/lib/api";
import type { SerializedBusinessUser } from "@/types";

export const Route = createFileRoute("/dashboard/clients/$id/users/$userId")({
  component: UserPage,
});

function UserPage() {
  const { id, userId } = Route.useParams();
  return <UserPageComponent businessId={id} userId={userId} />;
}

// Exported for testing
export function UserPageComponent({
  businessId: id,
  userId,
}: {
  businessId: string;
  userId: string;
}) {
  const navigate = useNavigate();
  const [user, setUser] = useState<SerializedBusinessUser | null>(null);
  const [ownerCount, setOwnerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const load = useCallback(() => {
    let active = true;
    fetchClient(id)
      .then((response) => {
        if (!active) return;
        const found = response.users.find((u) => u.userUid === userId);
        setOwnerCount(response.users.filter((u) => u.role === "owner").length);
        if (!found) {
          setNotFound(true);
        } else {
          setUser(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching client:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, userId]);

  useEffect(() => load(), [load]);

  const goToClient = () =>
    navigate({ to: "/dashboard/clients/$id", params: { id } });

  const handleDelete = async () => {
    if (!user) return;
    try {
      const result = await deleteClientUser(id, user.userUid);
      if (result.success) {
        toast.success("User deleted successfully");
        goToClient();
      } else {
        toast.error("Failed to delete user", { description: result.error });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete user";
      toast.error("Failed to delete user", { description: message });
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !user) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>User not found</div>
          </div>
        </div>
      </div>
    );
  }

  const isLastOwner = user.role === "owner" && ownerCount === 1;

  return (
    <EntityFormPage
      title="User"
      description={user.email || `User UID: ${user.userUid}`}
      backHref={`/dashboard/clients/${id}`}
      headerActions={
        showDeleteConfirm ? undefined : mode === "view" ? (
          <>
            <Button type="button" variant="outline" onClick={() => setMode("edit")}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </>
        )
      }
    >
      <div className="flex items-center gap-3 mb-6">
        <EntityAvatar type="user" name={user.email} className="w-12 h-12" />
        <div>
          <p className="font-medium">{user.email || `User UID: ${user.userUid}`}</p>
          <p className="text-sm text-muted-foreground">Role: {user.role || "member"}</p>
        </div>
      </div>

      {showDeleteConfirm ? (
        <DeleteConfirmPanel
          title="Delete User?"
          description="This will permanently delete the user account and remove their access to this client."
          confirmLabel="Delete User"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          disabled={isLastOwner}
          disabledReason="Warning: This is the last owner. You cannot delete this user."
        />
      ) : mode === "view" ? (
        <FormSection title="User Details">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ConfigField label="Email" value={user.email} />
            <ConfigField label="Role" value={user.role || "member"} />
          </div>
        </FormSection>
      ) : (
        <EditUserForm
          businessId={id}
          user={user}
          onSaved={() => {
            load();
            setMode("view");
          }}
        />
      )}
    </EntityFormPage>
  );
}
